<script setup lang="ts">
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useDeletePki } from "~/modules/pki/composables/useDeletePki";
import type { Pki } from "~/modules/pki/models/Pki.interface";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  pki: Pki;
}>();

const emit = defineEmits(["change"]);

const { isLoading, error, deletePki } = useDeletePki();
const { can } = useAcl();
const { t } = useI18n();
const [deleteConfirmDialogVisible, toggleDeleteConfirmVisible] = useToggle();

const deleteDisabled = computed(
  () => isLoading.value || !can(ACLRoles.CAN_WRITE_FEATURE),
);

const onDeletePkiClick = () => {
  toggleDeleteConfirmVisible(true);
};

const onDeletePkiConfirm = async () => {
  await deletePki(props.pki.alias);
  toggleDeleteConfirmVisible();

  if (error.value) {
    error.value.notify(NotificationKey.error);
  } else {
    showNotificationSuccess(t(NotificationKey.removeSuccess));
    emit("change");
  }
};
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :title="t('pki.delete.title')"
    :disabled="deleteDisabled"
    :icon="!isLoading ? ILxcLightTrash2 : undefined"
    class="invisible"
    @click.stop.prevent="onDeletePkiClick"
  >
    <lxc-loader v-if="isLoading" />
  </lxc-button>

  <confirm-modal
    :is-dialog-visible="deleteConfirmDialogVisible"
    :title="t('pki.delete.title')"
    :description="t('pki.delete.message')"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    :is-loading="isLoading"
    @cancel="toggleDeleteConfirmVisible"
    @confirm="onDeletePkiConfirm"
  />
</template>

<style scoped lang="scss">
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
